import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Star, TrendingUp, TrendingDown, Coins, Trash2, Loader2, ArrowRight,
  Plus, Pencil, Check, X,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import api from "../lib/api";
import { toast } from "sonner";

export default function Watchlist() {
  const [lists, setLists] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [items, setItems] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const loadLists = useCallback(async () => {
    const r = await api.get("/watchlists");
    const ls = r.data.watchlists || [];
    setLists(ls);
    setActiveId((prev) => prev || (ls.length ? ls[0].id : null));
    return ls;
  }, []);

  const loadItems = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await api.get(`/watchlist?watchlist_id=${id}`);
      const list = r.data.items || [];
      setItems(list);
      setQuotes({});
      Promise.allSettled(
        list.map((i) =>
          api.get(`/quote/${i.asset_type}/${i.symbol}?range=1M`)
            .then((r) => ({ id: i.id, data: r.data }))
        )
      ).then((results) => {
        const map = {};
        results.forEach((r) => {
          if (r.status === "fulfilled") map[r.value.id] = r.value.data;
        });
        setQuotes(map);
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLists(); }, [loadLists]);
  useEffect(() => { if (activeId) loadItems(activeId); }, [activeId, loadItems]);

  const createList = async () => {
    if (!newName.trim()) return;
    try {
      const r = await api.post("/watchlists", { name: newName.trim() });
      setLists([...lists, r.data]);
      setActiveId(r.data.id);
      setNewName("");
      setCreating(false);
      toast.success("List created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const renameList = async (id) => {
    if (!editName.trim()) { setEditingId(null); return; }
    try {
      await api.patch(`/watchlists/${id}`, { name: editName.trim() });
      await loadLists();
      setEditingId(null);
      toast.success("Renamed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deleteList = async (id) => {
    try {
      await api.delete(`/watchlists/${id}`);
      const remaining = lists.filter((l) => l.id !== id);
      setLists(remaining);
      if (activeId === id && remaining.length) setActiveId(remaining[0].id);
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/watchlist/${id}`);
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Failed to remove");
    }
  };

  const activeList = lists.find((l) => l.id === activeId);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 md:px-8 lg:px-12 py-10 md:py-14" data-testid="watchlist-page">
        <div className="flex items-end justify-between mb-8 animate-fade-up">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
              Your lists
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-[#2A2F2B] mt-1 flex items-center gap-3">
              Watchlists
              <Star className="w-7 h-7 text-[#D18E63] fill-[#D18E63]" strokeWidth={1.5} />
            </h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 flex-wrap mb-6">
          {lists.map((l) => (
            <div
              key={l.id}
              data-testid={`watchlist-tab-${l.id}`}
              className={`group flex items-center gap-1 rounded-full border transition-soft ${
                activeId === l.id
                  ? "bg-[#2A2F2B] border-[#2A2F2B] text-white"
                  : "bg-white border-[#EAE6E1] text-[#2A2F2B] hover:border-[#547A8F]"
              }`}
            >
              {editingId === l.id ? (
                <div className="flex items-center pl-3">
                  <input
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && renameList(l.id)}
                    className="bg-transparent outline-none text-sm py-2 w-28"
                    data-testid={`rename-input-${l.id}`}
                  />
                  <button onClick={() => renameList(l.id)} className="p-2" data-testid={`rename-save-${l.id}`}>
                    <Check className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                  <button onClick={() => setEditingId(null)} className="p-2">
                    <X className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => setActiveId(l.id)}
                    className="px-4 py-2 text-sm font-medium"
                    data-testid={`select-watchlist-${l.id}`}
                  >
                    {l.name}
                    <span className={`ml-2 text-xs font-mono-ticker ${activeId === l.id ? "text-white/60" : "text-[#6B7068]"}`}>
                      {l.item_count}
                    </span>
                  </button>
                  <button
                    onClick={() => { setEditingId(l.id); setEditName(l.name); }}
                    data-testid={`edit-watchlist-${l.id}`}
                    className={`p-1.5 opacity-0 group-hover:opacity-100 transition-soft ${activeId === l.id ? "text-white" : "text-[#6B7068]"}`}
                  >
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                  {!l.is_default && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`delete-watchlist-${l.id}`}
                          className={`p-1.5 pr-3 opacity-0 group-hover:opacity-100 transition-soft ${activeId === l.id ? "text-white" : "text-[#6B7068]"}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-2xl">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete "{l.name}"?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This list and all its items will be removed permanently.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteList(l.id)} className="rounded-full bg-[#C66B59] hover:bg-[#a85748]">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </>
              )}
            </div>
          ))}
          {creating ? (
            <div className="flex items-center gap-1 rounded-full border border-[#547A8F] bg-white pl-3">
              <input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createList()}
                placeholder="List name"
                className="bg-transparent outline-none text-sm py-2 w-32"
                data-testid="new-list-input"
              />
              <button onClick={createList} data-testid="new-list-save" className="p-2 text-[#5C8D73]"><Check className="w-4 h-4" strokeWidth={2} /></button>
              <button onClick={() => { setCreating(false); setNewName(""); }} className="p-2 pr-3"><X className="w-4 h-4" strokeWidth={2} /></button>
            </div>
          ) : (
            <button
              onClick={() => setCreating(true)}
              data-testid="add-watchlist-btn"
              className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-dashed border-[#EAE6E1] text-sm text-[#6B7068] hover:border-[#547A8F] hover:text-[#547A8F] transition-soft"
            >
              <Plus className="w-4 h-4" strokeWidth={1.75} /> New list
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#6B7068]"><Loader2 className="w-5 h-5 animate-spin" /> Loading...</div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl bg-white border border-[#EAE6E1] p-12 text-center" data-testid="watchlist-empty">
            <div className="w-16 h-16 rounded-full bg-[#F9F8F6] mx-auto flex items-center justify-center mb-4">
              <Star className="w-7 h-7 text-[#6B7068]" strokeWidth={1.25} />
            </div>
            <h3 className="font-heading text-2xl text-[#2A2F2B] mb-2">
              "{activeList?.name || "This list"}" is empty
            </h3>
            <p className="text-[#6B7068] mb-6 max-w-md mx-auto">
              Search for a stock or crypto and tap the star to start tracking your favorites.
            </p>
            <Link to="/dashboard" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white text-sm transition-soft">
              Explore markets <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-[#EAE6E1] overflow-hidden">
            <div className="hidden md:grid grid-cols-[1fr_120px_120px_120px_60px] gap-4 px-6 py-3 border-b border-[#EAE6E1] text-[10px] uppercase tracking-[0.22em] font-semibold text-[#6B7068]">
              <div>Asset</div><div className="text-right">Price</div><div className="text-right">1M</div><div className="text-right">High</div><div></div>
            </div>
            {items.map((item) => {
              const q = quotes[item.id];
              const isUp = (q?.change_pct ?? 0) >= 0;
              const trendColor = isUp ? "#5C8D73" : "#C66B59";
              return (
                <div
                  key={item.id}
                  data-testid={`watchlist-row-${item.symbol}`}
                  className="group grid md:grid-cols-[1fr_120px_120px_120px_60px] grid-cols-[1fr_auto] gap-4 items-center px-6 py-4 border-b border-[#EAE6E1] last:border-0 hover:bg-[#F9F8F6] transition-soft"
                >
                  <Link to={`/asset/${item.asset_type}/${item.symbol}`} className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: item.asset_type === "stock" ? "#547A8F15" : "#D18E6315" }}
                    >
                      {item.asset_type === "stock" ? (
                        <TrendingUp className="w-4 h-4 text-[#547A8F]" strokeWidth={1.75} />
                      ) : (
                        <Coins className="w-4 h-4 text-[#D18E63]" strokeWidth={1.75} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-mono-ticker font-semibold text-[#2A2F2B]">{item.symbol}</div>
                      <div className="text-xs text-[#6B7068] truncate">{item.name}</div>
                    </div>
                  </Link>
                  <div className="hidden md:block text-right font-mono-ticker text-[#2A2F2B]">
                    {q ? `$${q.price < 1 ? q.price.toFixed(4) : q.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                  </div>
                  <div className="hidden md:block text-right font-mono-ticker font-semibold" style={{ color: trendColor }}>
                    {q ? `${isUp ? "+" : ""}${q.change_pct.toFixed(2)}%` : "—"}
                  </div>
                  <div className="hidden md:block text-right font-mono-ticker text-[#6B7068] text-sm">
                    {q ? `$${q.high < 1 ? q.high.toFixed(4) : q.high.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    data-testid={`remove-watchlist-${item.symbol}`}
                    className="text-[#6B7068] hover:text-[#C66B59] p-2 rounded-full hover:bg-[#C66B59]/10 transition-soft"
                    aria-label="Remove"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
